import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Chip,
  Alert,
  IconButton,
} from "@mui/material";
import {
  CheckCircleOutline,
  Security,
  Settings,
  Assessment,
  PlayArrow,
  CheckCircle,
  ArrowForward,
  Close,
} from "@mui/icons-material";

const ProcessGuidePanel = ({ open, onClose }) => {
  const steps = [
    {
      id: 1,
      title: "Create Assessment",
      icon: <Assessment sx={{ color: "primary.main" }} />,
      subSteps: [
        {
          id: 1.1,
          description: "Start by creating a new workload assessment using the 'Create Assessment' button. Enter the account number for which you want to create the assessment for and select the primary region of your application/workload. Select multiple regions if you have resources deployed in regions other than the primary region.",
        },
        {
          id: 1.2,
          description: "As part of WAFR, a CIS AWS Benchmark 5.0 assessment will be initiated at least once to evaluate the security posture of your environment. If a CIS Benchmark is not available, it will be created during the creation of your WAFR assessment. This helps ensure alignment with AWS best practices and highlights your overall AWS security posture.",
        },
        {
          id: 1.3,
          description: "CloudCatcher checks the following AWS services that are recommended by AWS Well Architected Framework to provide appropriate automation results. Enable the missing services and rerun the create assessment process. If any of the services are not enabled due to internal policy, you can still create an assessment with Cloudcatcher native automation checks",
          services: [
            "AWS Business Support",
            "AWS Compute Optimizer", 
            "AWS Cost Optimization Hub",
            "AWS Config",
            "AWS Security Hub",
            "AWS Discovery"
          ],
        },
      ]
    },
    {
      id: 2,
      title: "Fill Assessment Details",
      description: "Enter the assessment name, description, assessment approver, and owner.",
      icon: <CheckCircle sx={{ color: "primary.main" }} />,
    },
    {
      id: 3,
      title: "Assessment Created",
      description: "You've successfully created a WAFR assessment!",
      icon: <CheckCircleOutline sx={{ color: "success.main" }} />,
    },
    {
      id: 4,
      title: "Run Automated Check",
      description: "After completing your assessment, you must run the automated check. If a CIS benchmark scan was initiated, you must run the automated check after your scan is completed. Once the run is completed, you will be able to view your assessment.",
      icon: <PlayArrow sx={{ color: "primary.main" }} />,
    },
    {
      id: 5,
      title: "Complete Manual Checks",
      description: "Once the automated checks are complete, complete the manual checks within the assessment.",
      icon: <CheckCircle sx={{ color: "primary.main" }} />,
    },
    {
      id: 6,
      title: "Complete Assessment",
      description: "After completing both manual and automated checks, verify and select 'Complete Assessment' to present record your progress.",
      icon: <ArrowForward sx={{ color: "success.main" }} />,
    },
  ];

  return (
    <>
      {/* Backdrop */}
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1299,
          }}
        />
      )}
      
      {/* Panel */}
      <Box
        sx={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 480,
          backgroundColor: "#fff",
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
          border: "1px solid #e0e0e0",
          zIndex: 1300,
          display: open ? "block" : "none",
          overflowY: "auto",
        }}
      >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#333",
                fontSize: "18px",
                mb: 1,
              }}
            >
              WAFR Create Assessment Process Guide
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "13px", lineHeight: 1.4, mb: 2 }}
            >
              Follow these steps to complete your workload assessment journey
            </Typography>
            
            {/* Important Notice */}
            <Alert 
              severity="info" 
              sx={{ 
                fontSize: "12px",
                "& .MuiAlert-message": {
                  fontSize: "12px",
                  lineHeight: 1.4
                }
              }}
            >
              Each AWS account is eligible for a maximum of one WAFR assessment. Please contact support.cloudcatcher@cloudnovatech.com to modify the limit
            </Alert>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "#e0e0e0",
                color: "#333",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 2 }}>

          <List sx={{ p: 0 }}>
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <ListItem sx={{ px: 0, py: 2, alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: 50, mt: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: step.icon.props.color === "success.main" ? "#e8f5e8" : "#f0f7ff",
                        border: `2px solid ${step.icon.props.color === "success.main" ? "#4caf50" : "#1976d2"}`,
                      }}
                    >
                      {step.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "15px",
                          }}
                        >
                          {step.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "13px",
                            lineHeight: 1.5,
                            mb: step.services ? 1.5 : 0,
                          }}
                        >
                          {step.description}
                        </Typography>
                        
                        {/* AWS Services List */}
                        {step.services && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "primary.main",
                                fontWeight: 600,
                                fontSize: "12px",
                                display: "block",
                                mb: 1,
                              }}
                            >
                              AWS Recommended Services:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {step.services.map((service, idx) => (
                                <Chip
                                  key={idx}
                                  label={service}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#f5f5f5",
                                    color: "text.secondary",
                                    fontSize: "11px",
                                    height: 24,
                                    "& .MuiChip-label": {
                                      px: 1,
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>

                {/* Sub-steps */}
                {step.subSteps && (
                  <Box sx={{ pl: 3, pr: 2 }}>
                    {step.subSteps.map((subStep, subIndex) => (
                      <Box key={subStep.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                              fontSize: "14px",
                              minWidth: "20px",
                              mt: 0.5,
                            }}
                          >
                            {subIndex + 1}.
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: "13px",
                                lineHeight: 1.5,
                                mb: subStep.services ? 1.5 : 0,
                              }}
                            >
                              {subStep.description}
                            </Typography>
                            
                            {/* AWS Services List for sub-steps */}
                            {subStep.services && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    display: "block",
                                    mb: 1,
                                  }}
                                >
                                  AWS Recommended Services:
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {subStep.services.map((service, idx) => (
                                    <Chip
                                      key={idx}
                                      label={service}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#f5f5f5",
                                        color: "text.secondary",
                                        fontSize: "10px",
                                        height: 22,
                                        "& .MuiChip-label": {
                                          px: 1,
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {index < steps.length - 1 && (
                  <Divider
                    sx={{
                      ml: 6,
                      mr: 2,
                      borderColor: "#e0e0e0",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </List>

        </Box>
      </Paper>
      </Box>
    </>
  );
};

export default ProcessGuidePanel;